// @greenlight-spec: 28-it-dashboard-ui.md#internal-health--internal-health-org-admin
// @greenlight-spec: 19-design-system.md
import * as React from 'react';

import { cn } from '@/lib/utils';

import type { HistoryMarker, HistoryPoint } from './historyModel';
import { bucketIndexOf } from './historyModel';
import { HistoryTable } from './HistoryTable';
import { formatMoment } from './historyTime';
import { OTHER_SERIES_COLOR } from '@/components/health/seriesColors';
import { SeriesDot } from '@/components/health/SeriesDot';

const W = 600;
const H = 100;

const TONES = {
  greenlight: 'hsl(var(--viz-greenlight))',
  apps: 'hsl(var(--viz-apps))',
} as const;

/** One of several lines on a chart: a replica or a resource, keyed by the dot beside its name. */
export interface HistoryLine {
  key: string;
  label: string;
  color: string;
  points: HistoryPoint[];
}

export interface HistoryChartProps {
  title: string;
  /** What the line is, in plain words: "Highest replica, % of its own limit". */
  caption: string;
  /** The combined reading: what gaps, the empty state, and a single line are drawn from. */
  points: HistoryPoint[];
  /** Drawn in place of `points`, each in the colour of the dot that keys it on the page. */
  lines?: HistoryLine[];
  /** Names the lines drawn in {@link OTHER_SERIES_COLOR}, which no dot on the page keys. */
  otherLinesLabel?: string;
  stepMs: number;
  format: (value: number) => string;
  /** The top of the scale; defaults to the highest value or reference, whichever is larger. */
  domainMax?: number;
  reference?: { value: number; label: string };
  markers: HistoryMarker[];
  mark?: 'line' | 'columns';
  tone?: keyof typeof TONES;
  /** Holding the previous render while the next one loads. */
  refreshing?: boolean;
  testId: string;
  /** A caveat on what the chart can show, such as replicas the read left out. */
  note?: string;
  /** Replaces "Nothing observed" when readings exist but cannot be drawn. */
  emptyMessage?: string;
}

/** Runs of consecutive unobserved buckets, shaded so a gap never reads as a quiet interval. */
function gapRuns(points: HistoryPoint[]): [number, number][] {
  const runs: [number, number][] = [];
  points.forEach((point, i) => {
    if ((point.avg ?? point.peak) !== null || point.noLimit) return;
    const last = runs[runs.length - 1];
    if (last && last[1] === i - 1) last[1] = i;
    else runs.push([i, i]);
  });
  return runs;
}

/** Runs of consecutive observed buckets; a gap between two runs is never bridged. */
function observedRuns(points: HistoryPoint[]): number[][] {
  const runs: number[][] = [];
  let run: number[] = [];
  points.forEach((point, i) => {
    if ((point.avg ?? point.peak) === null) {
      if (run.length > 0) runs.push(run);
      run = [];
    } else {
      run.push(i);
    }
  });
  if (run.length > 0) runs.push(run);
  return runs;
}

export function HistoryChart(props: HistoryChartProps): React.ReactElement {
  const { title, caption, points, stepMs, format, reference, markers, testId } = props;
  const mark = props.mark ?? 'line';
  const color = TONES[props.tone ?? 'greenlight'];
  const [active, setActive] = React.useState<number | null>(null);
  const tooltipId = React.useId();

  const lines = props.lines && props.lines.length > 0 ? props.lines : null;
  const values = [points, ...(lines ?? []).map((line) => line.points)]
    .flatMap((ps) => ps.flatMap((p) => [p.avg, p.peak]))
    .filter((v): v is number => v !== null);
  const max = Math.max(props.domainMax ?? 0, ...values, reference?.value ?? 0) || 1;
  const n = points.length;
  const xOf = (i: number): number => ((i + 0.5) / Math.max(n, 1)) * W;
  const yOf = (v: number): number => H - (Math.min(v, max) / max) * H;
  const half = W / Math.max(n, 1) / 2;
  const runs = observedRuns(points);
  const observed = runs.length > 0;

  const linePath = (
    run: number[],
    pick: (p: HistoryPoint) => number | null,
    source: HistoryPoint[] = points,
  ): string => {
    const coords = run.map((i) => [xOf(i), yOf(pick(source[i]!) ?? 0)] as const);
    // One observed bucket spans its own width rather than drawing as a dot.
    if (coords.length === 1) {
      const [x, y] = coords[0]!;
      return `M${x - half},${y}L${x + half},${y}`;
    }
    return coords.map(([x, y], k) => `${k === 0 ? 'M' : 'L'}${x},${y}`).join('');
  };

  const bandPath = (run: number[]): string | null => {
    const withBoth = run.filter((i) => points[i]!.avg !== null && points[i]!.peak !== null);
    if (withBoth.length !== run.length || run.length < 2) return null;
    const top = run.map((i) => `${xOf(i)},${yOf(points[i]!.peak!)}`);
    const bottom = [...run].reverse().map((i) => `${xOf(i)},${yOf(points[i]!.avg!)}`);
    return `M${top.join('L')}L${bottom.join('L')}Z`;
  };

  const placed = markers
    .map((m) => ({ ...m, index: bucketIndexOf(points, m.t, stepMs) }))
    .filter((m) => m.index !== -1);

  const moveTo = (index: number): void => setActive(Math.max(0, Math.min(n - 1, index)));
  const onPointer = (event: React.PointerEvent<HTMLDivElement>): void => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (rect.width <= 0) return;
    moveTo(Math.floor(((event.clientX - rect.left) / rect.width) * n));
  };
  const onKey = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const current = active ?? n - 1;
    const next =
      event.key === 'ArrowLeft'
        ? current - 1
        : event.key === 'ArrowRight'
          ? current + 1
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? n - 1
              : null;
    if (next === null) return;
    event.preventDefault();
    moveTo(next);
  };

  const point = active === null ? null : (points[active] ?? null);
  const otherLines =
    lines !== null &&
    props.otherLinesLabel !== undefined &&
    lines.some((line) => line.color === OTHER_SERIES_COLOR);
  const markersHere = placed.filter((m) => m.index === active);
  // A replica is listed from its first reading to its last, so one replaced earlier
  // is left out while a live replica's missed interval still reads as a gap. A keyed
  // line is one the page lists now, so it runs to the end of the range.
  const linesHere = (lines ?? []).filter((line) => {
    if (active === null) return false;
    const seen = (p: HistoryPoint): boolean => (p.avg ?? p.peak) !== null || p.noLimit === true;
    const running = line.color !== OTHER_SERIES_COLOR || line.points.slice(active).some(seen);
    return running && line.points.slice(0, active + 1).some(seen);
  });

  return (
    <figure
      className={cn(
        'rounded-gl-icon border border-border p-3.5 transition-opacity',
        props.refreshing && 'opacity-60',
      )}
      data-testid={testId}
    >
      <figcaption className="mb-2">
        <p className="text-[12.5px] font-semibold text-foreground">{title}</p>
        <p className="text-[11px] text-muted-foreground">{caption}</p>
      </figcaption>

      <div className="flex gap-2">
        <div className="flex w-10 shrink-0 flex-col justify-between py-px text-right">
          <span className="font-mono text-[10px] leading-none text-muted-foreground tabular-nums">
            {format(max)}
          </span>
          <span className="font-mono text-[10px] leading-none text-muted-foreground tabular-nums">
            {format(0)}
          </span>
        </div>

        <div
          className="relative h-24 min-w-0 flex-1 cursor-crosshair rounded-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          role="group"
          aria-label={`${title}: ${caption}. Use the arrow keys to read each interval.`}
          aria-describedby={point ? tooltipId : undefined}
          tabIndex={0}
          onPointerMove={onPointer}
          onPointerLeave={() => setActive(null)}
          onFocus={() => setActive((a) => a ?? n - 1)}
          onBlur={() => setActive(null)}
          onKeyDown={onKey}
        >
          <svg
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="none"
            className="absolute inset-0 h-full w-full overflow-visible"
            aria-hidden="true"
          >
            <line
              x1={0}
              x2={W}
              y1={H}
              y2={H}
              className="stroke-border"
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
            {reference && (
              <line
                x1={0}
                x2={W}
                y1={yOf(reference.value)}
                y2={yOf(reference.value)}
                className="stroke-slate/60"
                strokeWidth={1}
                strokeDasharray="4 3"
                vectorEffect="non-scaling-stroke"
              />
            )}
            {observed &&
              gapRuns(points).map(([first, last]) => (
                <rect
                  key={`gap-${first}`}
                  x={xOf(first) - half}
                  width={(last - first + 1) * 2 * half}
                  y={0}
                  height={H}
                  className="fill-slate/10"
                />
              ))}
            {/* Behind the data, so a marker never hides the reading it explains. */}
            {placed.map((m, k) => (
              <line
                key={`${m.kind}-${m.t}-${k}`}
                x1={xOf(m.index)}
                x2={xOf(m.index)}
                y1={0}
                y2={H}
                stroke={m.kind === 'incident' ? 'hsl(var(--status-red) / 0.55)' : undefined}
                className={m.kind === 'release' ? 'stroke-slate/60' : undefined}
                strokeWidth={1}
                strokeDasharray={m.kind === 'release' ? '3 3' : undefined}
                vectorEffect="non-scaling-stroke"
              />
            ))}
            {mark === 'columns'
              ? points.map((p, i) =>
                  p.peak === null ? null : (
                    <rect
                      key={i}
                      data-observed-zero={p.peak <= 0 ? '' : undefined}
                      x={xOf(i) - Math.min(half * 0.7, 12)}
                      width={Math.min(half * 1.4, 24)}
                      // An observed zero is a tick on the baseline, so it stays
                      // distinct from a shaded interval nobody observed.
                      y={p.peak <= 0 ? H - 2 : yOf(p.peak)}
                      height={p.peak <= 0 ? 2 : H - yOf(p.peak)}
                      fill={color}
                      fillOpacity={p.peak <= 0 ? 0.35 : 1}
                    />
                  ),
                )
              : lines
                ? // Unkeyed lines first, so a keyed one is never hidden under them.
                  [...lines]
                    .sort(
                      (a, b) =>
                        Number(b.color === OTHER_SERIES_COLOR) -
                        Number(a.color === OTHER_SERIES_COLOR),
                    )
                    .flatMap((line) =>
                      observedRuns(line.points).map((run) => (
                        <path
                          key={`${line.key}-${run[0]}`}
                          data-line={line.key}
                          d={linePath(run, (p) => p.avg ?? p.peak, line.points)}
                          fill="none"
                          stroke={line.color}
                          strokeWidth={line.color === OTHER_SERIES_COLOR ? 1.5 : 2}
                          strokeLinejoin="round"
                          strokeLinecap="round"
                          vectorEffect="non-scaling-stroke"
                        />
                      )),
                    )
                : runs.map((run) => {
                    const band = bandPath(run);
                    return (
                      <g key={run[0]}>
                        {band && <path d={band} fill={color} fillOpacity={0.1} />}
                        <path
                          d={linePath(run, (p) => p.avg ?? p.peak)}
                          fill="none"
                          stroke={color}
                          strokeWidth={2}
                          strokeLinejoin="round"
                          strokeLinecap="round"
                          vectorEffect="non-scaling-stroke"
                        />
                      </g>
                    );
                  })}
            {active !== null && (
              <line
                x1={xOf(active)}
                x2={xOf(active)}
                y1={0}
                y2={H}
                className="stroke-foreground/40"
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
            )}
          </svg>

          {placed
            .filter((m) => m.kind === 'incident')
            .map((m, k) => (
              <span
                key={`${m.t}-${k}`}
                aria-hidden="true"
                className="pointer-events-none absolute -top-1.5 -ml-[4px] text-[8px] leading-none text-[hsl(var(--status-red))]"
                style={{ left: `${(xOf(m.index) / W) * 100}%` }}
              >
                ▼
              </span>
            ))}

          {!observed && (
            <p className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">
              {props.emptyMessage ?? 'Nothing observed in this range.'}
            </p>
          )}

          {point && (
            <div
              id={tooltipId}
              role="status"
              className="pointer-events-none absolute top-0 z-10 min-w-[10rem] rounded-md border border-border bg-popover px-2.5 py-1.5 text-[11px] shadow-sm"
              style={{
                left: `${(xOf(active!) / W) * 100}%`,
                transform: `translate(${xOf(active!) > W / 2 ? '-100%' : '0'}, -100%)`,
              }}
            >
              {lines ? (
                <>
                  <p className="text-muted-foreground">{formatMoment(point.t)}</p>
                  {linesHere.length === 0 && (
                    <p className="font-semibold text-foreground">Not observed</p>
                  )}
                  {linesHere.map((line) => (
                    <p key={line.key} className="flex items-center gap-1.5 text-foreground">
                      <SeriesDot color={line.color} />
                      <span className="min-w-0 truncate text-muted-foreground">{line.label}</span>
                      <span className="ml-auto pl-2 font-semibold tabular-nums">
                        {readout(line.points[active!]!, format)}
                      </span>
                    </p>
                  ))}
                </>
              ) : (
                <>
                  <p className="font-semibold text-foreground">{readout(point, format)}</p>
                  <p className="text-muted-foreground">{formatMoment(point.t)}</p>
                  {point.scope && <p className="text-muted-foreground">{point.scope}</p>}
                </>
              )}
              {markersHere.map((m, k) => (
                <p key={k} className="text-foreground">
                  {m.kind === 'incident' ? '▼ ' : '┆ '}
                  {m.label}
                </p>
              ))}
            </div>
          )}
        </div>
      </div>

      {(placed.length > 0 || reference || otherLines) && (
        <p className="mt-2 flex flex-wrap gap-x-3 pl-12 text-[10.5px] text-muted-foreground">
          {reference && <span>┄ {reference.label}</span>}
          {otherLines && <span>— {props.otherLinesLabel}</span>}
          {placed.some((m) => m.kind === 'incident') && (
            <span>
              <span className="text-[hsl(var(--status-red))]" aria-hidden="true">
                ▼
              </span>{' '}
              Incident
            </span>
          )}
          {placed.some((m) => m.kind === 'release') && <span>┆ Release</span>}
        </p>
      )}

      {props.note && (
        <p className="mt-1.5 pl-12 text-[10.5px] text-muted-foreground">{props.note}</p>
      )}

      <HistoryTable
        title={title}
        points={points}
        {...(lines ? { lines } : {})}
        format={format}
        markers={placed}
      />
    </figure>
  );
}

/** Values lead: the number first, then what it is. */
export function readout(point: HistoryPoint, format: (value: number) => string): string {
  if (point.avg === null && point.peak === null) {
    return point.noLimit ? 'Observed, no limit set' : 'Not observed';
  }
  if (point.avg !== null && point.peak !== null && point.avg !== point.peak) {
    return `avg ${format(point.avg)} · peak ${format(point.peak)}`;
  }
  return format((point.peak ?? point.avg)!);
}
